import { formatDateLong, formatPrice } from "@/utils/format";
import { Order } from "@/utils/types/types";
import { Button, Modal } from "@mantine/core";
import { Link } from "react-router-dom";

import LineItemInsert from "@/components/OrderInserts/LineItemInsert";

function CustomerDetails({ customer = {}, className }: any) {
  const { name, email, phone } = customer;
  const address = [
    customer?.address?.line1,
    customer?.address?.line2,
    customer?.address?.city,
    customer?.address?.postal_code,
    customer?.address?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className={`text-muted ${className}`}>
      {name && <p>{name}</p>}
      {address && <p>{address}</p>}
      {email && <p>{email}</p>}
      {phone && <p>{phone}</p>}
    </div>
  );
}

type OrderDetailsModalProps = {
  order?: Order;
  opened: boolean;
  close: () => void;
};

export default function OrderDetailsModal({
  order,
  opened,
  close,
}: OrderDetailsModalProps) {
  if (!order) return null;
  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Order Details"
      size="xl"
      styles={{ body: { padding: "2rem", display: "grid" } }}
    >
      <div className="flex justify-between text-muted">
        <p>Order: #{order.displayOrderNo || order.orderNo}</p>
        <p>Date: {formatDateLong(new Date(order.createdAt))}</p>
        <p>Status: {order.stripeOrder?.status || "pending"}</p>
      </div>

      <p className="mt-8 title-20">Items</p>
      <div className="divide-y">
        {order.eventData.lineItems.map((item: any, index: any) => {
          const [title, ...tags] = [
            item.cartData?.staticField1,
            item.cartData?.staticField2,
            item.cartData?.staticField3,
            item.cartData?.staticField4,
            ...(item.cartData?.conditionalFields || []),
          ];
          return (
            <div
              key={index}
              className={`grid ${
                item.imageUrl
                  ? "grid-cols-[auto_1fr_auto]"
                  : "grid-cols-[1fr_auto]"
              } gap-4 py-4`}
            >
              {/* 2026-07-31: no image box when there is no image — custom
                  (hand-quoted) legacy items have imageUrl "" and rendered a
                  broken-image icon. The grid drops the image column in that
                  case so the price stays right-aligned. onError covers the
                  other case: a URL whose ASF-hosted file has died collapses
                  to text instead of a broken icon (the anchor stays, its
                  auto column shrinks to nothing). */}
              {item.imageUrl ? (
                <a
                  href={item.imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2"
                >
                  <img
                    src={item.imageUrl}
                    alt={title}
                    className="size-24"
                    onError={e => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </a>
              ) : null}
              <div key={index} className="grid gap-1">
                <p className="font-bold">{title}</p>
                {tags
                  .filter(el => !!el)
                  .map((tag, index) => (
                    <p key={index} className="ml-2 text-muted">
                      &middot; {tag}
                    </p>
                  ))}
                {/* Insert / COA for this print (2026-10-18). */}
                {!order.isLegacy && item.id && (
                  <div className="mt-1">
                    <LineItemInsert orderId={order.id} lineItemId={item.id} />
                  </div>
                )}
              </div>
              <div className="flex flex-col text-muted">
                <p>
                  Price:{" "}
                  {item.quantity > 1
                    ? `${formatPrice(item.price * item.quantity)} (${formatPrice(item.price)}/print)`
                    : formatPrice(item.price * item.quantity)}
                </p>
                <p>Quantity: {item.quantity}</p>
              </div>
            </div>
          );
        })}
        <p className="flex items-center gap-4 py-4">
          Total:{" "}
          <span className="ml-auto">
            {formatPrice(Number(order.eventData.totalPrice))}
          </span>
        </p>
      </div>
      <p className="mt-6 title-20">Customer Details</p>
      <CustomerDetails
        customer={order.stripeCustomerDetails || order.eventData}
        className="mt-2"
      />

      {order.stripeShippingDetails && (
        <>
          <p className="mt-6 title-20">Shipping Details</p>
          <CustomerDetails
            customer={order.stripeShippingDetails}
            className="mt-2"
          />
        </>
      )}

      {order.stripeOrder?.charges?.data?.[0] && (
        <>
          <p className="mt-6 title-20">Billing Details</p>
          <div className="mt-2">
            <p className="text-muted">
              Payment method:{" "}
              {
                order.stripeOrder?.charges?.data?.[0]?.payment_method_details
                  ?.type
              }
            </p>
            <CustomerDetails
              customer={order.stripeOrder?.charges?.data?.[0]?.billing_details}
            />
          </div>
        </>
      )}
      {/* <pre>{JSON.stringify(order, null, 2)}</pre> */}
      {order.zipUrl && (
        <Button component={Link} to={order.zipUrl} className="ml-auto mt-6">
          Download Invoice
        </Button>
      )}
    </Modal>
  );
}
